import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";

interface KioskClockProps {
  /** Locale for time/date formatting; defaults to the browser locale. */
  locale?: string;
  className?: string;
}

/** Near-black ambient screensaver clock for the wall tablet. */
export function KioskClock({ locale, className }: KioskClockProps) {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    // 1s tick keeps the minute flip prompt; display self-corrects from new Date() each tick.
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const time = now.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" });
  const date = now.toLocaleDateString(locale, {
    weekday: "long",
    day: "numeric",
    month: "long",
  });

  return (
    // bg-black is intentional: true #000 for OLED wall tablets, not a missing token
    <div
      data-slot="kiosk-clock"
      role="timer"
      aria-label={`Current time: ${time}`}
      className={cn(
        "flex h-full w-full flex-col items-center justify-center gap-3 bg-black text-white/90 sm:gap-4",
        className,
      )}
    >
      {/* Time is the primary element: oversized, tabular-nums to prevent digit jitter on tick */}
      <span className="text-8xl leading-none font-extralight tracking-tight tabular-nums sm:text-9xl md:text-[12rem]">
        {time}
      </span>
      {/* Date sits below as a quiet, spaced subordinate label */}
      <span className="text-base font-light tracking-[0.2em] text-white/40 uppercase sm:text-lg md:text-xl">
        {date}
      </span>
    </div>
  );
}
